import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IonCol,
  IonItem,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonSpinner,
} from '@ionic/react';
import {
  selectCrateState,
  updateTrackField,
} from '../../store/slices/crate.slice';
import { AppDispatch } from '../../store/store';

interface EditableKeyFieldProps {
  trackId: string;
  keyVal: string | undefined;
  className?: string;
  textColor?: string;
}

const camelotKeys = Array.from({ length: 12 }, (_, i) => `${i + 1}A`).concat(
  Array.from({ length: 12 }, (_, i) => `${i + 1}B`),
);

// Mixed In Key color scheme for Camelot wheel
const getKeyColor = (camelotKey: string | undefined): string => {
  if (!camelotKey) return '#666666';

  const keyColors: { [key: string]: string } = {
    '1A': '#B8E6B8',
    '1B': '#B8E6B8', // Light green
    '2A': '#C8E6A0',
    '2B': '#C8E6A0', // Yellow-green
    '3A': '#E6E6A0',
    '3B': '#E6E6A0', // Light yellow
    '4A': '#E6D0A0',
    '4B': '#E6D0A0', // Light orange/peach
    '5A': '#E6B8A0',
    '5B': '#E6B8A0', // Light salmon
    '6A': '#E6A0B8',
    '6B': '#E6A0B8', // Light pink
    '7A': '#E6A0D0',
    '7B': '#E6A0D0', // Light rose
    '8A': '#D0A0E6',
    '8B': '#D0A0E6', // Light purple
    '9A': '#B8A0E6',
    '9B': '#B8A0E6', // Medium purple
    '10A': '#A0B8E6',
    '10B': '#A0B8E6', // Light blue-purple
    '11A': '#A0D0E6',
    '11B': '#A0D0E6', // Light blue
    '12A': '#A0E6E6',
    '12B': '#A0E6E6', // Light cyan
  };

  return keyColors[camelotKey] || '#666666';
};

export const EditableKeyField: React.FC<EditableKeyFieldProps> = ({
  trackId,
  keyVal,
  className = '',
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isEditable } = useSelector(selectCrateState);
  const [displayValue, setDisplayValue] = useState<string | undefined>(keyVal);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDisplayValue(keyVal);
  }, [keyVal]);

  const keyColor = useMemo(() => getKeyColor(displayValue), [displayValue]);

  const handleChange = async (selectedKey: string) => {
    if (selectedKey === keyVal || isSaving) return;
    setDisplayValue(selectedKey);
    setIsSaving(true);

    try {
      const result = await dispatch(
        updateTrackField({
          trackId,
          field: 'Key_Camelot',
          value: selectedKey,
        }),
      );

      if (!updateTrackField.fulfilled.match(result)) {
        setDisplayValue(keyVal);
      }
    } catch (error) {
      setDisplayValue(keyVal);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <IonRow
      className={className}
      style={{ width: '100%', alignItems: 'center' }}
    >
      <IonCol size="12">
        {isEditable ? (
          <IonItem lines="none" style={{ '--background': 'transparent' }}>
            <IonSelect
              value={displayValue}
              placeholder="--"
              onIonChange={(e) => handleChange(e.detail.value)}
              className={className}
              disabled={isSaving}
              style={{
                textAlign: 'center',
                flex: 1,
                minWidth: '6ch',
                maxWidth: '9ch',
                color: keyColor,
                fontWeight: 600,
                opacity: isSaving ? 0.6 : 1,
              }}
            >
              {camelotKeys.map((key) => (
                <IonSelectOption key={key} value={key}>
                  {key}
                </IonSelectOption>
              ))}
            </IonSelect>
            {isSaving && (
              <IonSpinner
                name="crescent"
                style={{ marginLeft: '8px', width: '16px', height: '16px' }}
              />
            )}
          </IonItem>
        ) : (
          <span
            className="key-value"
            style={{
              color: keyColor,
              fontWeight: 600,
              textAlign: 'center',
              display: 'block',
            }}
          >
            {displayValue ?? '--'}
          </span>
        )}
      </IonCol>
    </IonRow>
  );
};
